import { useMediaQuery } from 'react-responsive'
import { Button, Icon, Loader, Placeholder, Popup } from 'semantic-ui-react'
import Link from 'next/link'
import { gql, useMutation, useQuery } from '@apollo/client'

import styles from './style.module.css'
import Logo from './Logo'
import DigitalNomad from '@assets/digitalnomad'
import { createApplication } from '@src/graphql/mutations'
import { useAuth } from '@src/auth'
import { getJobSeekerIdForUser } from 'queries'
import Tag from './Tag'
import TabletApplicationCard from '@components/TabletApplicationCard'
import MobileApplicationCard from '@components/MobileApplicationCard'
import ErrorBoundary from '@components/ErrorBoundary'
import fromNow from '@src/helpers/fromNow'
import AppLink from './AppLink'
import CoverLetterButton from './CoverLetterButton'
import ResumeLink from './ResumeLink'

export default function ApplicationCard(props) {
  const {
    post,
    status,
    active,
    applicationDate,
    onClick,
    empty,
    showApplicationDate,
    collapsed,
    dashboardCard,
    shouldRenderApplied
  } = props
  const { user, isCandidate } = useAuth()

  const [
    apply,
    { data: updateData, loading: applicationLoading, error, reset },
  ] = useMutation(gql(createApplication))

  const { data: userData, loading } = useQuery(gql(getJobSeekerIdForUser), {
    variables: { userId: user?.username },
    // refetchQueries: [getCandidateById]
  })

  const jobSeekerId = userData?.getUserById?.jobSeeker?.id

  const isTablet = useMediaQuery({ query: '(max-width: 1024px)' })
  const isMobile = useMediaQuery({ query: '(max-width: 500px)' })

  const applyToJob = (e) => {
    e.stopPropagation()
    e.preventDefault()

    if (jobSeekerId) {
      const vars = {
        variables: {
          input: {
            id: (Math.random() * 1000).toString(),
            jobId: post?.id,
            jobSeekerId,
          },
        },
      }

      apply(vars)
    } else {
      // router.push('/signup')
    }
  }

  if (empty) {
    return <EmptyApplicationCard />
  }

  const {
    description = '',
    jobTitle,
    jobLogo,
    companyName,
    companyId,
    companyProfile,
    digitalNomadFriendly,
    willSponsor,
    featured,
    applied,
    scraped,
    scrapedUrl,
  } = post

  if (jobLogo === 'https://www.builtin.comundefined') return null



  const renderApplyNow = () => {
    if (collapsed) return null;
     
    if (status === 'ACCEPTED') {
      return (
        <div className={styles.accepted}>
          <b>Application Accepted</b>
        </div>
      )
    } else if (status === 'DECLINED') {
      return (
        <div className={styles.declined}>
          <b>Application Declined</b>
        </div>
      )
    }

    if (applied || applicationDate) {
      return (
        <a href={scrapedUrl} target="_blank" rel="noopener noreferrer">
          <Button circular color="blue" size="small">
            Applied{' '}
            <Icon name="linkify" style={{ marginLeft: '8px', marginRight: '0' }} />
          </Button>
        </a>
      )
    } else if (applicationLoading) {
      return (
        <Button
          circular
          color="blue"
          disabled
          onClick={applyToJob}
          size="small"
        >
          Apply Now{' '}
          <Loader
            size="mini"
            inverted
            active
            inline
            style={{ marginLeft: '8px', marginRight: '0' }}
          ></Loader>
        </Button>
      )
    }

    if (scraped && scrapedUrl) {
      return (
        <a target="_blank" href={scrapedUrl} rel="noopener noreferrer">
          <Button circular color="blue" size="small">
            Apply Now{' '}
            <Icon
              name="location arrow"
              style={{ marginLeft: '8px', marginRight: '0' }}
            />
          </Button>
        </a>
      )
    }

    if (!user) {
      return (
        <Link href={'/signup'}>
          <Button circular color="blue" size="small">
            Apply Now{' '}
            <Icon
              name="location arrow"
              style={{ marginLeft: '8px', marginRight: '0' }}
            />
          </Button>
        </Link>
      )
    }

    if (post)
      return (
        <Button circular color="blue" onClick={applyToJob} size="small">
          Apply Now{' '}
          <Icon
            name="location arrow"
            style={{ marginLeft: '8px', marginRight: '0' }}
          />
        </Button>
      )
  }

  if (isMobile) {
    return <MobileApplicationCard {...props} />
  }

  if (isTablet) {
    return <TabletApplicationCard {...props} />
  }

  function renderDatePosted(post) {
    if (collapsed) return null; 

    if (shouldRenderApplied) {
      if (post?.dateApplied) {
        let result = fromNow(new Date(post?.dateApplied))
        if (result) {
          let retString = `Applied ${result}`
          if(retString === 'Applied 1 days ago') {
            return 'Applied yesterday'
          } 

          return retString
        }
      }
    }

    let result = fromNow(new Date(post?.datePosted))
    if (result) {
      return `Posted ${result}`
    }
  }

  return (
    <ErrorBoundary>
      <div
        className={`${styles.cardContainer} ${
          active ? styles.activeCard : ''
        } ${featured ? styles.featured : ''} ${collapsed ? styles.collapsed : ''}`}
        onClick={onClick}
      >
        <div className={`${styles.textContainer} ${collapsed ? styles.collapsedText : ''}`}>
          <div className={`${styles.title} ${collapsed ? styles.collapsedTitle : ''}`}>{jobTitle}</div>
          <div className={`${styles.companyName} ${collapsed ? styles.collapsedCompanyName : ''}`}>{companyName}</div>
          <div className={styles.description}>
            <div className={styles.labelsContainer}>
              {getApplicationLabels(post, true, collapsed)}
            </div>
          </div>
          <div className={styles.applyButton}>{renderApplyNow()}</div>
          {dashboardCard && <div className={styles.appLink}><AppLink post={post} dashboardCard /></div>}
          {dashboardCard && <div className={styles.coverLetterButton}><CoverLetterButton post={post} dashboardCard /></div>}
          {(dashboardCard && post?.generatedResumeUrl) && <div className={styles.resumeLink}><ResumeLink post={post} dashboardCard /></div>}
          {showApplicationDate && applicationDate && (
            <div className={styles.details}>
              <div className={styles.applicationDate}>
                {`Applied ${applicationDate}`}
              </div>
            </div>
          )}
        </div>

        <div className={styles.statsContainer}>
          <div className={styles.logoWrap}>
            <Logo
              image={jobLogo ?? companyProfile?.image}
              companyId={companyId}
              active={active}
              wrapperStyle={{
                width: '70px',
                height: '70px',
              }}
            />
          </div>
          <div className={styles.sourceInfo}>{renderDatePosted(post)}</div>
        </div>
      </div>
    </ErrorBoundary>
  )
}

const getApplicationLabels = (postData, standardWidth, collapsed) => {
  const labels = []

  const {
    startSalaryRangeThousands: startRange,
    endSalaryRangeThousands: endRange,
  } = postData

  if (startRange && !collapsed) {
    let salaryText = ''

    if (startRange && endRange) {
      salaryText = `$${startRange}k - $${endRange}k`
    } else {
      salaryText = `$${startRange}k`
    }

    labels.push(
      <Tag
        key={`${postData?.id}-salary`}
        standardWidth={standardWidth}
        color="green"
        icon="dollar sign"
        text={salaryText}
        doNotTrim
      />,
    )
  }

  if (postData?.scrapedSource === 'yc') {
    labels.push(
      <Tag
        key={`${postData?.id}-parttime`}
        standardWidth={standardWidth}
        color="teal"
        icon="YC"
        text={'YCombinator'}
      />,
    )
  }

  switch (postData?.roleType) {
    case 'PARTTIME':
      labels.push(
        <Tag
          key={`${postData?.id}-parttime`}
          standardWidth={standardWidth}
          color="teal"
          icon="hourglass"
          text={'Part Time'}
        />,
      )
      break
    case 'FULLTIME':
      labels.push(
        <Tag
          key={`${postData?.id}-fulltime`}
          standardWidth={standardWidth}
          color="teal"
          icon="hourglass"
          text={'Full Time'}
        />,
      )
      break
    case 'CONTRACT':
      labels.push(
        <Tag
          key={`${postData?.id}-contract`}
          standardWidth={standardWidth}
          color="teal"
          icon="hourglass"
          text={'Contract'}
        />,
      )
      break
    default:
      break
  }

  switch (postData?.remotePolicy) {
    case 'HYBRIDREMOTE':
      labels.push(
        <Tag
          key={`${postData?.id}-hybrid`}
          standardWidth={standardWidth}
          color="grey"
          icon="balance scale"
          text="Hybrid"
        />,
      )
      break
    case 'FULLREMOTE':
      labels.push(
        <Tag
          key={`${postData?.id}-fullremote`}
          standardWidth={standardWidth}
          color="grey"
          icon="wifi"
          text="100% Remote"
        />,
      )
      break
    case 'REMOTEOPTIONAL':
      labels.push(
        <Tag
          key={`${postData?.id}-optional`}
          standardWidth={standardWidth}
          color="grey"
          icon="wifi"
          text="Remote Optional"
        />,
      )
      break
    case 'ONSITE':
      labels.push(
        <Tag
          key={`${postData?.id}-onsite`}
          standardWidth={standardWidth}
          color="black"
          icon="building outline"
          text="Onsite"
        />,
      )
      break      
    default:
      break
  }

  if (postData?.willSponsor) {
    labels.push(
      <Tag
        key={`${postData?.id}-willSponsor`}
        standardWidth={standardWidth}
        color="green"
        icon="check"
        text="Will Sponsor"
      />,
    )
  }

  if (postData?.officeLocations?.length) {
    if (postData?.officeLocations?.length > 1) {
      const locs = postData?.officeLocations
      labels.push(
        <Tag
          standardWidth={standardWidth}
          key={`${postData?.id}-loc`}
          color="orange"
          icon="map pin"
          text={`${locs.length} Locations`}
        />,
      )
    } else {
      // console.log('postData: ', postData)
      labels.push(
        <Tag
          standardWidth={standardWidth}
          key={`${postData?.id}-${postData?.officeLocations[0]?.formatted_address}`}
          color="orange"
          icon="map pin"
          text={postData?.officeLocations[0]?.formatted_address}
        />,
      )
    }
  }

  if (postData?.digitalNomadFriendly) {
    labels.push(
      <Tag
        key={`${postData?.id}-dnfriendly`}
        standardWidth={standardWidth}
        color="green"
        icon="plane"
        text="Nomad Friendly"
      />,
    )
  }

  if (postData.featured) {
    labels.push(
      <Tag
        key={`${postData?.id}-featured`}
        standardWidth={standardWidth}
        color="yellow"
        icon="star"
        text="Featured"
      />,
    )
  }

  return labels
}

function EmptyApplicationCard() {
  const isTabletOrMobile = useMediaQuery({ query: '(max-width: 820px)' })

  const PlaceholderExampleFluid = () => (
    <Placeholder fluid>
      <Placeholder.Header>
        <Placeholder.Line />
        <Placeholder.Line />
      </Placeholder.Header>
      <Placeholder.Paragraph>
        <Placeholder.Line />
        <Placeholder.Line />
        <Placeholder.Line />
      </Placeholder.Paragraph>
    </Placeholder>
  )

  if (isTabletOrMobile) {
    return (
      <div className={`${styles.mobileCardContainer}`}>
        <div className={styles.wrapper}>
          <PlaceholderExampleFluid />
        </div>
      </div>
    )
  }

  return (
    <div className={`${styles.cardContainer}`}>
      <div className={styles.wrapper}>
        <PlaceholderExampleFluid />
      </div>
    </div>
  )
}
